/**
 * UserSettingsPage.jsx
 *
 * This component is a page for managing user settings. It includes editable fields for the user's name, email, password, language, and timezone.
 * Each field is represented by an EditableSettingItem component, which allows the user to edit the value of the field.
 * The component uses local state to store the current values of the fields and a honeypot field for spam protection.
 * The handleSave function is used to validate and save the new values entered by the user.
 * In the future, this component will be integrated with a backend to persist the changes made by the user.
 */

import React, { useContext, useEffect, useState } from "react";
import UserContext from "../../../context/UserContext";
import { useNavigate } from "react-router-dom";
import "./UserSettingsPage.scss";
import {
  getUserSettingsById,
  getLanguageOptions,
  updateUserLastNameInSettingsById,
  updateUserFirstNameInSettingsById,
  updateUserEmailInSettingsById,
  updateUserPasswordInSettingsById,
  updateUserLanguageInSettingsById,
  updateUserTimezoneInSettingsById,
  updateUser2FAEnabledInSettingsById,
} from "../../../api/settingsUserApi";
import { getUserByEmail } from "../../../api/userApi";
import EditableSettingItem from "../Shared/EditableSettingItem/EditableSettingItem";
import RedButton from "../../Shared/RedButton/RedButton";
import { LogoutOutlined } from "../../../helpers/icons/antDesignIcons";
import { toast } from "react-toastify";
import useTranslation from "../../../hooks/useTranslation";
import ToggleButton from "../Shared/ToggleButton/ToggleButton";
import TwoFactorSetup from "../../LoginPage/2FARelated/TwoFactorSetup";

const UserSettingsPage = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const { t } = useTranslation("settingsPage");
  const [lastname, setLastName] = useState("");
  const [firstname, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [language, setLanguage] = useState("");
  const [timezone, setTimezone] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [languageOptions, setLanguageOptions] = useState([]);
  const [timezoneOptions, setTimezoneOptions] = useState([1, 2, 3, 4, 5]);
  const [isPassword, setIsPassword] = useState(false);
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [showSetup2FA, setShowSetup2FA] = useState(false);

  const fetchUserSettings = async () => {
    // Fetch user settings from the backend
    const response = await getUserSettingsById(user.userId);
    // Update the state with the fetched values
    setLastName(response.lastname);
    setFirstName(response.firstname);
    setEmail(response.email);
    setLanguage(response.language);
    setTimezone(response.timezone_offset);
    setIs2FAEnabled(response.is_2fa_enabled);
  };

  // Fetch user settings when the component mounts
  useEffect(() => {
    fetchUserSettings();
  }, []);

  const fetchLanguageOptions = async () => {
    // Fetch language options from the backend
    const response = await getLanguageOptions();
    setLanguageOptions(response);
  };

  // Fetch language options when the component mounts
  useEffect(() => {
    fetchLanguageOptions();
  }, []);

  const updateUserLastName = async (lastname) => {
    try {
      await updateUserLastNameInSettingsById(lastname);
      localStorage.setItem("lastname", lastname);
      toast.success(t("update_user_lastname_success"));
    } catch (error) {
      toast.error(t("update_user_lastname_error"));
    }
  };

  const updateUserFirstName = async (firstname) => {
    try {
      await updateUserFirstNameInSettingsById(firstname);
      localStorage.setItem("firstname", firstname);
      toast.success(t("update_user_firstname_success"));
    } catch (error) {
      toast.error(t("update_user_firstname_error"));
    }
  };

  const updateEmail = async (email) => {
    try {
      const newEmail = await getUserByEmail(email);
      if (newEmail) {
        toast.error(t("update_user_email_error_invalid"));
        return;
      } else {
        await updateUserEmailInSettingsById(email);
        localStorage.setItem("email", email);
        toast.success(t("update_user_email_success"));
      }
    } catch (error) {
      console.log("Error: ", error);
      toast.error(t("update_user_email_error"));
      throw new Error(error);
    }
  };

  const updatePassword = async (password) => {
    try {
      await updateUserPasswordInSettingsById(user.userId, password);
      toast.success("Jelszó sikeresen frissítve!");
    } catch (error) {
      toast.error("Hiba történt a jelszó frissítése közben.");
    }
  };

  const updateLanguage = async (language) => {
    try {
      await updateUserLanguageInSettingsById(user.userId, language);
      localStorage.setItem("language", language);
      toast.success(t("update_user_language_success"));
      window.location.reload();
    } catch (error) {
      toast.error(t("update_user_language_error"));
    }
  };

  const updateTimezone = async (timezone) => {
    const formattedTimezone = timezone.replace("UTC+", "");
    try {
      await updateUserTimezoneInSettingsById(user.userId, formattedTimezone);
      localStorage.setItem("timezone_offset", formattedTimezone);
      toast.success("Időzóna sikeresen frissítve!");
    } catch (error) {
      toast.error("Hiba történt az időzóna frissítése közben.");
    }
  };

  const update2FAEnabled = async (newValue) => {
    // If the user wants to enable 2FA, show the setup component
    if (newValue === true) {
      setShowSetup2FA(true);
      return;
    }

    // If the user wants to disable 2FA, update the value in the database and the state
    try {
      await updateUser2FAEnabledInSettingsById(user.userId, newValue);
      setIs2FAEnabled(newValue);
      toast.success(t("update_user_2fa_success"));
    } catch (error) {
      toast.error(t("update_user_2fa_error"));
    }
  };

  // Handler for when 2FA setup is complete
  const handle2FASetupSuccess = async () => {
    try {
      await updateUser2FAEnabledInSettingsById(user.userId, false);
      setIs2FAEnabled(false);
      setShowSetup2FA(false);
      toast.success(t("update_user_2fa_success"));
    } catch (error) {
      toast.error(t("update_user_2fa_error"));
    }
  };

  const handle2FASetupCancel = async () => {
    setShowSetup2FA(false);
  };

  // Handle the save action for the editable fields
  const handleSave = async (newValue, id) => {
    let isValid = true;
    let errorMessage = "";

    if (honeypot) return;

    if (id === "lastname" && (newValue.trim() === "" || newValue.length > 50)) {
      isValid = false;
      errorMessage = t("update_user_lastname_error_invalid");
    } else if (
      id === "firstname" &&
      (newValue.trim() === "" || newValue.length > 50)
    ) {
      isValid = false;
      errorMessage = t("update_user_firstname_error_invalid");
    } else if (id === "email" && !/^\S+@\S+\.\S+$/.test(newValue)) {
      isValid = false;
      errorMessage = t("update_user_email_error_invalid");
    } else if (id === "password" && newValue.length < 14) {
      isValid = false;
      errorMessage =
        "A jelszónak legalább 14 karakter hosszúnak kell lennie és tartalmaznia kell legalább egy kis- és nagybetűt valamint egy különleges karaktert.";
    } else if (id === "timezone" && newValue.length > 500) {
      isValid = false;
      errorMessage = "Description should be less than 500 characters.";
    } else if (
      id === "language" &&
      !languageOptions
        .map((option) => option.language_code.toUpperCase())
        .includes(newValue.toUpperCase())
    ) {
      isValid = false;
      errorMessage = t("select_language");
    } else if (
      id === "timezone" &&
      !timezoneOptions.map((option) => `UTC+${option}`).includes(newValue)
    ) {
      isValid = false;
      errorMessage = "Kérjük válasszon a listából.";
    }

    if (!isValid) {
      toast.error(errorMessage); // show the error message
      return;
    }

    try {
      switch (id) {
        case "lastname":
          await updateUserLastName(newValue);
          setLastName(newValue);
          break;
        case "firstname":
          await updateUserFirstName(newValue);
          setFirstName(newValue);
          break;
        case "email":
          await updateEmail(newValue);
          setEmail(newValue);
          break;
        case "password":
          await updatePassword(newValue);
          break;
        case "language":
          await updateLanguage(newValue);
          setLanguage(newValue);
          break;
        case "timezone":
          await updateTimezone(newValue);
          setTimezone(localStorage.getItem("timezone_offset"));
          break;
        default:
          break;
      }
    } catch (error) {
      console.error("Hiba történt a módosítás közben.");
    }
  };

  return (
    <div className="container user-settings-page__container navbar-margin">
      <h1 className="page-title">{t("account_settings")}</h1>
      {/* Show 2FA setup if needed */}
      {showSetup2FA ? (
        <TwoFactorSetup
          onSuccess={handle2FASetupSuccess}
          onCancel={handle2FASetupCancel}
        />
      ) : (
        <div className="user-settings-page__content">
          <ToggleButton
            title={t("twoFactorAuth")}
            value={is2FAEnabled}
            onSave={update2FAEnabled}
            id="2FAEnabled"
          />
          <EditableSettingItem
            id="lastname"
            title={t("lastname")}
            value={lastname}
            onSave={handleSave}
            type="text"
          />
          <EditableSettingItem
            id="firstname"
            title={t("firstname")}
            value={firstname}
            onSave={handleSave}
            type="text"
          />
          <EditableSettingItem
            id="email"
            title={t("email")}
            value={email}
            onSave={handleSave}
            type="email"
          />
          <EditableSettingItem
            id="password"
            title={t("password")}
            value={password}
            onSave={handleSave}
            type="password"
            isPassword={true}
          />
          <EditableSettingItem
            id="language"
            title={t("language")}
            value={language.toUpperCase()}
            onSave={handleSave}
            type="dropdown"
            options={languageOptions.map((option) =>
              option.language_code.toUpperCase()
            )}
          />
          {/* <EditableSettingItem
          id="timezone"
          title={t("timezone")}
          value={`UTC+${timezone}`}
          onSave={handleSave}
          type="dropdown"
          options={timezoneOptions.map((option) => `UTC+${option}`)}
        /> */}
          <input
            type="text"
            name="honeypot"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            style={{ display: "none" }}
          />
        </div>
      )}
      <RedButton
        icon={<LogoutOutlined />}
        content={t("logout")}
        onClick={() => {
          localStorage.clear();
          navigate("/");
        }}
      />
    </div>
  );
};

export default UserSettingsPage;
